import React from 'react';
import {
  Badge,
  Card, CardSkeleton, HStack, ICON_ALERT_CIRCLE_OUTLINE, ICON_LIST_BOX_OUTLINE, Message,
  Spacing,
  Stack,
  SimpleTable,
  SimpleTableSkeleton,
} from '@doveit/bricks';
import { Task, TaskReference } from '../../../providers/api/dtos';
import useTasks from '../../hooks/use-tasks/useTasks';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';

import { referenceTypeLabels } from '../../../labels';
import UpsertTaskAction from '../upsert-task-action/UpsertTaskAction';

import ViewTaskTableRow from '../view-task-table-row/ViewTaskTableRow';
import { GetTasksFilters } from '../../../providers/api/task/taskProvider';
import { TaskStatus } from '../../../domain/types';
import ViewTaskCard from '../view-task-card/ViewTaskCard';
import { DEFAULT_PAGINATION_SIZE } from '../../../utils/querystring/querystring';
import { SortOrder } from '../../../providers/pagination';

export interface TasksWidgetProps extends React.AriaAttributes {
  reference: Omit<TaskReference, 'user'>,
  onTaskCreated?: (task: Task) => void,
  onTaskUpdated?: (task: Task) => void,
}

const FILTERS = ['active', 'done', 'all'] as const;

type Filter = typeof FILTERS[number];

const filterLabels: Record<Filter, string> = {
  all: 'Tutte',
  active: 'Attive',
  done: 'Completate',
};

export const LOAD_TASKS_ERROR_MESSAGE = 'Non è stato possibile caricare le attività.';
export const NO_TASKS_MESSAGE = 'Non sono presenti attività.';

const TasksWidget: React.FC<TasksWidgetProps> = ({
  reference,
  onTaskCreated,
  onTaskUpdated,
  ...rest
}) => {
  const isMobile = useIsDevice('mobile');
  const [activeFilter, setActiveFilter] = React.useState<Filter>(isMobile ? 'all' : 'active');

  const filters: GetTasksFilters = React.useMemo(() => {
    const baseFilters: GetTasksFilters = {
      reference,
    };

    if (isMobile) {
      return baseFilters;
    }

    switch (activeFilter) {
      case 'active':
        baseFilters.status = [TaskStatus.TODO, TaskStatus.IN_PROGRESS];
        break;
      case 'done':
        baseFilters.status = [TaskStatus.DONE];
        break;
      default:
        break;
    }

    return baseFilters;
  }, [activeFilter, isMobile, reference]);

  const {
    data: tasks,
    isLoading: areTasksLoading,
    error: tasksError,
    mutate: mutateTasks,
  } = useTasks(filters, {
    size: DEFAULT_PAGINATION_SIZE,
    sort: {
      createdAt: SortOrder.DESC,
    },
  });

  const title = React.useMemo(
    () => `Attività ${referenceTypeLabels[reference?.type].toLocaleLowerCase()}`,
    [reference?.type],
  );

  const handleTaskCreate = React.useCallback(async (task: Task) => {
    await mutateTasks();

    onTaskCreated?.(task);
  }, [mutateTasks, onTaskCreated]);

  const handleTaskUpdated = React.useCallback(async (task: Task) => {
    await mutateTasks();

    onTaskUpdated?.(task);
  }, [mutateTasks, onTaskUpdated]);

  if (areTasksLoading) {
    return (
      <CardSkeleton aria-label="Attività in caricamento">
        {!isMobile && <SimpleTableSkeleton />}
      </CardSkeleton>
    );
  }

  if (tasksError) {
    return (
      <Card>
        <Card.Header
          icon={{ path: ICON_ALERT_CIRCLE_OUTLINE }}
          title={title}
          color="critical"
        />
        <Card.Content>
          <Message message={LOAD_TASKS_ERROR_MESSAGE} />
        </Card.Content>
      </Card>
    );
  }

  return tasks && (
    <Card
      aria-label="Sezione attività"
      {...rest}
    >
      <Card.Header
        title={title}
        icon={{ path: ICON_LIST_BOX_OUTLINE }}
        primaryActions={[
          <UpsertTaskAction
            task={{ reference }}
            onSuccess={handleTaskCreate}
          />,
        ]}
      />
      <Card.Content>
        <Stack aria-label="Lista attività">
          {(isMobile && tasks.content.length > 0) && (
            tasks.content.map((task) => (
              <ViewTaskCard
                key={task.id}
                task={task}
                onTaskUpdated={handleTaskUpdated}
              />
            ))
          )}
          {!isMobile && (
            <>
              <Spacing margin={[0, 0, 150]}>
                <HStack aria-label="Filtri">
                  {FILTERS.map(((filter) => (
                    <Badge
                      label={filterLabels[filter]}
                      aria-label={filterLabels[filter]}
                      key={filter}
                      onClick={filter !== activeFilter ? () => setActiveFilter(filter) : undefined}
                      color={activeFilter === filter ? 'primary' : 'neutral'}
                      emphasis="low"
                    />
                  )))}
                </HStack>
              </Spacing>

              <SimpleTable>
                <SimpleTable.Body>
                  {tasks.content.map((task) => (
                    <SimpleTable.Row
                      asChild
                      key={task.id}
                    >
                      <ViewTaskTableRow
                        task={task}
                        onTaskUpdated={handleTaskUpdated}
                      />
                    </SimpleTable.Row>
                  ))}
                </SimpleTable.Body>
              </SimpleTable>
            </>
          )}
          {tasks.content.length === 0 && (
            <Message message={NO_TASKS_MESSAGE} />
          )}
        </Stack>
      </Card.Content>
    </Card>
  );
};

export default TasksWidget;
